// Sign-in (password, then six-digit code), accepting an invitation, and setting up two-factor sign-in.
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'qrcode';
import { ApiError, get, post } from '../api';
import { useAuth } from '../auth';
import type { Me } from '../types';
import { ROLE_LABELS } from '../types';
import { useToast } from '../ui';

const Logo = () => <img className="logo" src="/assets-static/logo.png" alt="Blue Market" />;

export const SSO_ERRORS: Record<string, string> = {
  expired: 'That sign-in took too long or was started in another browser. Try again.',
  cancelled: 'Sign-in was cancelled.',
  failed: "We couldn't confirm that account. Try again, or use your password.",
  blocked: "Your account can't sign in right now. Ask whoever invited you.",
  taken: 'That account is already connected to someone else here.',
  'unknown-google': "This Google account isn't connected to an account here yet. Sign in with your password, then connect it from your Account page.",
  'unknown-microsoft': "This Microsoft account isn't connected to an account here yet. Sign in with your password, then connect it from your Account page.",
};

/** Read and clear #sso-…=… left by a Google/Microsoft redirect (a fragment: never sent to a server or in a Referer). */
export function takeSsoHash(): Record<string, string> {
  const m = /^#(sso-[a-z]+)=(.+)$/.exec(location.hash);
  if (!m) return {};
  history.replaceState(null, '', location.pathname + location.search);
  return { [m[1]]: decodeURIComponent(m[2]) };
}

function SsoButtons() {
  const [providers, setProviders] = useState<{ name: string; label: string }[]>([]);
  useEffect(() => { get<{ providers: { name: string; label: string }[] }>('/api/auth/sso/providers').then((r) => setProviders(r.providers)).catch(() => {}); }, []);
  if (!providers.length) return null;
  return (
    <div className="stack sso" style={{ gap: 8 }}>
      <div className="sso-or"><span>or</span></div>
      {providers.map((p) => <a key={p.name} className="btn block sso-btn" href={`/api/auth/sso/${p.name}/start`}>Continue with {p.label}</a>)}
    </div>
  );
}

export function SignIn() {
  const { refresh } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challenge, setChallenge] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  useEffect(() => {
    const h = takeSsoHash();
    if (h['sso-code']) setChallenge(h['sso-code']);
    if (h['sso-error']) setError(SSO_ERRORS[h['sso-error']] ?? 'That sign-in didn\'t work. Try again.');
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      if (!challenge) {
        const r = await post<{ needs_code: boolean; challenge?: string }>('/api/auth/login', { email, password });
        if (r.needs_code && r.challenge) { setChallenge(r.challenge); setPassword(''); }
        else await refresh();
      } else {
        const r = await post<{ recovery_codes_left: number | null }>('/api/auth/login/code', { challenge, code });
        if (r.recovery_codes_left !== null && r.recovery_codes_left !== undefined) {
          toast(r.recovery_codes_left <= 3 ? `Signed in with a recovery code. Only ${r.recovery_codes_left} left: make new ones on your Account page.` : `Signed in with a recovery code. ${r.recovery_codes_left} left.`, r.recovery_codes_left <= 3);
        }
        await refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      if (err instanceof ApiError && challenge && err.status !== 401) { setChallenge(null); setCode(''); }
    } finally { setBusy(false); }
  };

  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Logo />
        <div><div className="eyebrow">Blue Market</div><h1>{challenge ? 'Enter your code' : 'Sign in'}</h1></div>
        {!challenge ? (
          <>
            <label className="field"><span>Email</span><input className="input" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus /></label>
            <label className="field"><span>Password</span><input className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required /></label>
          </>
        ) : (
          <>
            <p className="muted small" style={{ margin: 0 }}>{useRecovery ? 'Enter one of your recovery codes (e.g. K7QM-4XRT-2PLW). Each code works once.' : 'Open your authenticator app and enter the six-digit code for Blue Market.'}</p>
            <input className="input code-input" style={useRecovery ? { letterSpacing: '.12em', fontSize: 20 } : undefined} inputMode={useRecovery ? 'text' : 'numeric'} autoComplete="one-time-code" maxLength={useRecovery ? 16 : 7} value={code} onChange={(e) => setCode(e.target.value)} autoFocus required aria-label={useRecovery ? 'Recovery code' : 'Six-digit code'} />
            <button type="button" className="btn ghost sm" onClick={() => { setUseRecovery(!useRecovery); setCode(''); setError(''); }}>{useRecovery ? 'Use the authenticator app instead' : 'Lost your phone? Use a recovery code'}</button>
          </>
        )}
        {error && <div className="error">{error}</div>}
        <button className="btn primary lg block" disabled={busy}>{busy && <span className="spin" />}{challenge ? 'Verify' : 'Sign in'}</button>
        {challenge && <button type="button" className="btn ghost sm" onClick={() => { setChallenge(null); setCode(''); setError(''); }}>Use a different account</button>}
        {!challenge && <SsoButtons />}
      </form>
    </div>
  );
}

export function AcceptInvite() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [token] = useState(() => decodeURIComponent(location.hash.slice(1)));
  const [info, setInfo] = useState<{ email: string; name: string | null; role: keyof typeof ROLE_LABELS; client: string | null; invited_by: string | null } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // The token travels after "#", so it never reaches server logs. Clear it from the address bar once read.
    history.replaceState(null, '', location.pathname);
    if (token.length < 20) { setError('This invitation link is incomplete. Open the full link from your invitation.'); return; }
    post<NonNullable<typeof info>>('/api/invitations/lookup', { token })
      .then((i) => { setInfo(i); setName(i.name ?? ''); })
      .catch((e) => setError(e.message));
  }, [token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await post<{ user: Me }>('/api/invitations/accept', { token, name, password });
      await refresh();
      navigate('/');
    } catch (err) { setError(err instanceof Error ? err.message : 'Something went wrong'); }
    finally { setBusy(false); }
  };

  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Logo />
        <div><div className="eyebrow">Invitation</div><h1>Join Blue Market</h1></div>
        {info && (
          <p className="muted small" style={{ margin: 0 }}>
            {info.invited_by ? `${info.invited_by} invited ` : 'You have been invited as '}<b>{info.email}</b>{info.invited_by ? ' to join' : ''} as <b>{ROLE_LABELS[info.role]}</b>{info.client ? <> for <b>{info.client}</b></> : null}.
          </p>
        )}
        {info && (
          <>
            <label className="field"><span>Your name</span><input className="input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" required /></label>
            <label className="field"><span>Choose a password</span><input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" minLength={12} required /><span className="hint">At least 12 characters. A passphrase works well.</span></label>
          </>
        )}
        {error && <div className="error">{error}</div>}
        {info && <button className="btn primary lg block" disabled={busy}>{busy && <span className="spin" />}Create my account</button>}
      </form>
    </div>
  );
}

export function TwoFactorSetup() {
  const { me, refresh, signOut } = useAuth();
  const [secret, setSecret] = useState('');
  const [qr, setQr] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    post<{ secret: string; uri: string }>('/api/auth/2fa/setup')
      .then(async (r) => { setSecret(r.secret); setQr(await QRCode.toDataURL(r.uri, { margin: 1, width: 200 })); })
      .catch((e) => setError(e.message));
  }, []);

  const [codes, setCodes] = useState<string[] | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { setCodes((await post<{ recovery_codes: string[] }>('/api/auth/2fa/enable', { code })).recovery_codes); }
    catch (err) { setError(err instanceof Error ? err.message : 'Something went wrong'); }
    finally { setBusy(false); }
  };

  if (codes) return <div className="auth-wrap"><div className="card auth-card stack"><Logo /><RecoveryCodes codes={codes} onDone={refresh} /></div></div>;
  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Logo />
        <div><div className="eyebrow">Security</div><h1>Set up two-factor sign-in</h1></div>
        <p className="muted small" style={{ margin: 0 }}>
          Blue Market accounts need a second step at sign-in. Scan this code with an authenticator app (1Password, Google Authenticator, Microsoft Authenticator), then enter the six-digit code it shows.
        </p>
        {qr && <div className="qr"><img src={qr} alt="QR code for your authenticator app" width={200} height={200} /></div>}
        {secret && <p className="tiny muted" style={{ margin: 0 }}>Can't scan? Enter this key: <span className="mono">{secret.match(/.{1,4}/g)?.join(' ')}</span></p>}
        <input className="input code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e) => setCode(e.target.value)} required aria-label="Six-digit code" />
        {error && <div className="error">{error}</div>}
        <button className="btn primary lg block" disabled={busy || !secret}>{busy && <span className="spin" />}Turn on two-factor sign-in</button>
        <button type="button" className="btn ghost sm" onClick={signOut}>Sign out ({me?.email})</button>
      </form>
    </div>
  );
}

export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);
  const text = `Blue Market recovery codes\nEach code works once, in place of your authenticator app.\n\n${codes.join('\n')}\n`;
  const download = () => {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([text], { type: 'text/plain' }));
    a.download = 'blue-market-recovery-codes.txt';
    a.click();
    URL.revokeObjectURL(a.href);
    setSaved(true);
  };
  return (
    <div className="stack">
      <div><div className="eyebrow">Two-factor sign-in is on</div><h1>Save your recovery codes</h1></div>
      <p className="muted small" style={{ margin: 0 }}>If you lose your phone, each of these codes lets you sign in once. <b>They're shown only now.</b> Keep them in your password manager or somewhere safe.</p>
      <div className="recovery-codes mono">{codes.map((c) => <span key={c}>{c}</span>)}</div>
      <div className="row">
        <button type="button" className="btn grow" onClick={download}>Download</button>
        <button type="button" className="btn grow" onClick={async () => { try { await navigator.clipboard.writeText(text); setSaved(true); } catch { /* select manually */ } }}>Copy</button>
      </div>
      <label className="row small"><input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />I've saved these codes somewhere safe</label>
      <button className="btn primary lg block" disabled={!saved} onClick={onDone}>Continue</button>
    </div>
  );
}

export function ResetPassword() {
  const navigate = useNavigate();
  const [token] = useState(() => decodeURIComponent(location.hash.slice(1)));
  const [info, setInfo] = useState<{ email: string; name: string } | null>(null);
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    history.replaceState(null, '', location.pathname);
    if (token.length < 20) { setError('This reset link is incomplete. Open the full link you were sent.'); return; }
    post<{ email: string; name: string }>('/api/auth/reset/lookup', { token }).then(setInfo).catch((e) => setError(e.message));
  }, [token]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError('');
    try { await post('/api/auth/reset', { token, password }); setDone(true); }
    catch (err) { setError(err instanceof Error ? err.message : 'Something went wrong'); }
    finally { setBusy(false); }
  };
  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={submit}>
        <Logo />
        <div><div className="eyebrow">Password</div><h1>{done ? 'Password changed' : 'Choose a new password'}</h1></div>
        {done ? (
          <>
            <p className="muted small" style={{ margin: 0 }}>You've been signed out on every device. Sign in with your new password.</p>
            <button type="button" className="btn primary lg block" onClick={() => navigate('/')}>Go to sign-in</button>
          </>
        ) : info && (
          <>
            <p className="muted small" style={{ margin: 0 }}>For <b>{info.email}</b>.</p>
            <label className="field"><span>New password</span><input className="input" type="password" autoComplete="new-password" minLength={12} value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus /><span className="hint">At least 12 characters.</span></label>
          </>
        )}
        {error && <div className="error">{error}</div>}
        {!done && info && <button className="btn primary lg block" disabled={busy}>{busy && <span className="spin" />}Set new password</button>}
      </form>
    </div>
  );
}
